import { forwardRef, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/components/ui/cn";

const VDialog = DialogPrimitive.Root;
const VDialogTrigger = DialogPrimitive.Trigger;
const VDialogClose = DialogPrimitive.Close;

const VDialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-40 bg-neutral-900/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out",
      className,
    )}
    {...props}
  />
));
VDialogOverlay.displayName = "VDialogOverlay";

type VDialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  title?: string;
  description?: string;
  size?: "sm" | "md" | "lg" | "xl";
  children: ReactNode;
};

const sizeClass = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

const VDialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  VDialogContentProps
>(({ className, title, description, size = "md", children, ...props }, ref) => {
  const hasHeader = Boolean(title || description);
  return (
    <DialogPrimitive.Portal>
      <VDialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-border bg-card shadow-lg",
          sizeClass[size],
          className,
        )}
        {...props}
      >
        {hasHeader ? (
          <div className="shrink-0 px-6 pb-2 pr-12 pt-6">
            {title ? (
              <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
                {title}
              </DialogPrimitive.Title>
            ) : null}
            {description ? (
              <DialogPrimitive.Description className="mt-1 text-sm text-muted-foreground">
                {description}
              </DialogPrimitive.Description>
            ) : null}
          </div>
        ) : null}
        <div
          className={cn(
            "min-h-0 flex-1 overflow-y-auto px-6",
            hasHeader ? "pb-6 pt-2" : "py-6",
          )}
        >
          {children}
        </div>
        <DialogPrimitive.Close
          aria-label="Sluiten"
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <X className="size-4" aria-hidden />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
VDialogContent.displayName = "VDialogContent";

export { VDialog, VDialogTrigger, VDialogContent, VDialogClose };
